import {
  externalLaunchContextAtom,
  ExternalLaunchType,
} from '@/atoms/externalLaunchAuthAtoms';
import { workspaceHydrationGatePorts } from '@/composition/hosts/workspace-hydration-gate-ports';
import { useMultiWorkspaceSelectionGate } from '@/features/flags';
import { useSyncActiveWorkspaceToDesktop } from '@/hooks/desktop/useSyncActiveWorkspaceToDesktop';
import { usePostLogoutRedirect } from '@/hooks/usePostLogoutRedirect';
import { useSourceControlReadinessFailOpen } from '@/hooks/useSourceControlReadinessFailOpen';
import { useSourceControlReadinessWriter } from '@/hooks/useSyncSourceControlSelections';
import { GdtLauncher } from '@/integrations/gdt/components/gdt-launcher';
import { MedicalDirector } from '@/integrations/medical-director/medicalDirector';
import { T2medLauncher } from '@/integrations/t2med/components/t2med-launcher';
import { AuthProvider } from '@/models/authModel';
import type { RootState } from '@/store';
import { isUsingReactKinde } from '@/tauri/utils';
import { AUTH_PROVIDER_COOKIE } from '@/utils/auth/authProviderCookie';
import { isKeycloak } from '@/utils/buildProfile';
import { WorkspaceHydrationGate } from '@heidi/workspace';
import { useHydrateWorkspaceId } from '@heidi/workspace/data';
import { getCookie } from 'cookies-next/client';
import { useAtomValue } from 'jotai';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React from 'react';
import { useSelector } from 'react-redux';
import { AuthTokenResolver, UserResolver } from './auth';
import FhirAuth from './fhir-auth';
import { KindePropertiesQueryProvider } from './kinde-properties-query-provider';
import TauriAuth from './tauriAuth';

// Lazy-import via next/dynamic so `next-auth/react` (transitively imported by
// KeycloakAuthShell) is never evaluated in the cloud bundle. A static
// `import { KeycloakAuthShell }` pulls `next-auth/react/index.js` into the
// module graph, and that module runs `parseUrl(process.env.NEXTAUTH_URL)` at
// top level — cloud builds don't set NEXTAUTH_URL, so the import-time call
// throws `TypeError: Invalid URL` and breaks every page render.
//
// `ssr: false` is required (the default `ssr: true` is not sufficient).
// With `ssr: true`, Next.js still bundles the chunk into the server output
// and requires it during SSR via `__webpack_require__.a` — the
// `next-auth/react` top-level code fires on the Node side regardless of
// whether the component actually renders. With `ssr: false`, the chunk is
// excluded from the server bundle entirely; `<KeycloakAuthShell>` renders
// to `null` server-side and lazy-hydrates on the client only when reached
// in the JSX tree (i.e. only on on-prem builds where `isKeycloak === true`).
//
// `<SessionProvider>` is a pure client-side React context — the session is
// hydrated from cookies on the client — so opting out of SSR has no
// behavioural cost.
const KeycloakAuthShell = dynamic(
  () =>
    import('@/components/onprem/KeycloakAuthShell').then((m) => ({
      default: m.KeycloakAuthShell,
    })),
  { ssr: false }
);

type AuthBranch = 'fhir' | 'keycloak' | 'tauri' | 'fhir-cookie' | 'web';

/**
 * The ONE ordered decision of which auth shell renders. The render below
 * switches on it, and so does the workspace-gate deferral above the early
 * returns — a second predicate restating these conditions would drift from the
 * order (e.g. desktop is decided before the FHIR cookie is even looked at).
 */
function resolveAuthBranch({
  pathname,
  authProvider,
  token,
  authProviderCookie,
  isGenericSmartOnFhirLaunch,
}: {
  pathname: string | undefined;
  authProvider: AuthProvider | undefined;
  token: string | undefined;
  authProviderCookie: string | undefined;
  isGenericSmartOnFhirLaunch: boolean;
}): AuthBranch {
  // FhirAuth for Haiku routes (which bypass Kinde auth) and FHIR launches.
  if (
    pathname?.includes('haiku') ||
    authProvider === AuthProvider.FHIR ||
    isGenericSmartOnFhirLaunch
  ) {
    return 'fhir';
  }
  if (isKeycloak) return 'keycloak';
  if (isUsingReactKinde()) return 'tauri';
  // FHIR launch tabs can retain a token after the provider state is cleared.
  // Keep FhirAuth while the FHIR cookie identifies that launch session.
  if (token && authProvider === undefined && authProviderCookie === AuthProvider.FHIR) {
    return 'fhir-cookie';
  }
  return 'web';
}

export function AuthWrapper({ children }: { children: React.ReactNode }) {
  const { authProvider, token } = useSelector((rootState: RootState) => rootState.auth);
  const externalLaunchContext = useAtomValue(externalLaunchContextAtom);
  const authProviderCookie =
    typeof window !== 'undefined' ? getCookie(AUTH_PROVIDER_COOKIE) : undefined;
  const router = useRouter();
  const isGenericSmartOnFhirLaunch =
    externalLaunchContext?.launchType === ExternalLaunchType.GENERIC_SMART_ON_FHIR;
  const authBranch = resolveAuthBranch({
    pathname: router?.pathname,
    authProvider,
    token,
    authProviderCookie,
    isGenericSmartOnFhirLaunch,
  });
  // The branches that mount `WorkspaceHydrationGate`, decided BEFORE the
  // early returns so the boot writer can be told about it. The post-login
  // workspace selector only ever renders from that gate: deferring the
  // default pick on a branch without one would leave the atom null for the whole
  // session, with every request header-less.
  const mountsWorkspaceGate = authBranch === 'web' || authBranch === 'tauri';
  // An existing multi-workspace user following an invite link while logged out
  // lands here freshly signed in: auto-pick as before and let the accept flow
  // switch them, rather than interrupting the join with the selector.
  const isJoinTeamRoute = router?.pathname?.startsWith('/join-team') ?? false;
  const { shouldDeferInitialPick } = useMultiWorkspaceSelectionGate();
  usePostLogoutRedirect();
  useHydrateWorkspaceId({
    deferInitialPickWhenMultiple:
      mountsWorkspaceGate && !isJoinTeamRoute && shouldDeferInitialPick,
  });
  useSyncActiveWorkspaceToDesktop();
  // Both halves of the Evidence readiness gate, and both MUST stay above every
  // auth-branch early return below. The writer also has to sit above the
  // workspace-hydration gate each branch mounts further down: that gate holds
  // its whole subtree behind a loader for the cold-boot window, so a writer
  // owned by a branch is absent for exactly the stretch the watchdog is timing.
  // Branches publish their auth state into the writer instead of owning one.
  useSourceControlReadinessFailOpen();
  useSourceControlReadinessWriter();

  if (authBranch === 'fhir') {
    return (
      <KindePropertiesQueryProvider>
        <FhirAuth>{children}</FhirAuth>
      </KindePropertiesQueryProvider>
    );
  }

  // On-prem (Keycloak) builds bypass the Kinde-based wrappers entirely.
  // KeycloakAuthShell mounts NextAuth's SessionProvider (web) + the
  // KeycloakReduxSync bridge that writes the access token into Redux for
  // the HTTP interceptors (MOD-885 T6). Cloud builds dead-code-eliminate
  // this branch.
  //
  // Intentional omission: on-prem deployments do NOT mount the EHR
  // launchers `<GdtLauncher />` (German healthcare GDT protocol) or
  // `<MedicalDirector />` (Australian EHR). The initial on-prem target
  // is a single-tenant hospital install behind an airgap; those
  // integrations call out to vendor-hosted SaaS endpoints that
  // wouldn't reach anything from the airgapped network. When an on-prem
  // deployment is shipped to a hospital that *does* use one of these
  // EHRs, mount the relevant launcher inside `<KeycloakAuthShell>`
  // alongside `{children}` — and revisit this comment.
  if (authBranch === 'keycloak') {
    return (
      <KindePropertiesQueryProvider>
        <KeycloakAuthShell>{children}</KeycloakAuthShell>
      </KindePropertiesQueryProvider>
    );
  }

  if (authBranch === 'tauri') {
    return (
      <KindePropertiesQueryProvider>
        <TauriAuth>
          {children}
          <GdtLauncher />
          <T2medLauncher />
        </TauriAuth>
      </KindePropertiesQueryProvider>
    );
  }

  if (authBranch === 'fhir-cookie') {
    return (
      <KindePropertiesQueryProvider>
        <FhirAuth>{children}</FhirAuth>
      </KindePropertiesQueryProvider>
    );
  }

  // The workspace-hydration gate sits BETWEEN the two halves of the auth
  // bootstrap. `AuthTokenResolver` (above the gate) resolves the access token
  // and lands it in Redux, which is exactly what the gate reads to decide
  // whether to fetch `GET /workspaces`. `UserResolver` (below the gate) owns the
  // request-firing hooks (`useQueryJoinedTeams`, `useQueryUserForContext`,
  // billing/specialties/templates) that must stay behind a resolved
  // `Heidi-Workspace-Id`, so it only mounts once hydration resolves. Keeping the
  // token resolver above the gate means the token survives while the
  // `UserResolver` subtree is held back.
  return (
    <AuthTokenResolver>
      <KindePropertiesQueryProvider>
        <WorkspaceHydrationGate ports={workspaceHydrationGatePorts}>
          <UserResolver>
            {children}
            {/* Integrations */}
            <MedicalDirector />
          </UserResolver>
        </WorkspaceHydrationGate>
      </KindePropertiesQueryProvider>
    </AuthTokenResolver>
  );
}
